import { Component, DoCheck, AfterViewInit, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Pagination, ContentProvider, Company } from '@lighty';
import { EmitterService, LoaderService, StorageService, ParamsService, TranslateService } from '@services';
import { SubscriptionCollection } from '@classes';
import { DataHelper } from '@helpers';
import * as moment from 'moment';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { gsap } from 'gsap';

@Component({
    selector: 'msc-learn-content-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss']
})

export class LearnContentListComponent implements DoCheck, AfterViewInit {
    private subscriptionCollection: SubscriptionCollection = new SubscriptionCollection();
    private me: any;
    private company: Company;
    public contents: any;
    public allContents: any[] = []; // Full unfiltered content list for status counting
    public events: any;
    public contentsCollection: any;
    public pagination: Pagination;
    public paginationEvent: Pagination;
    public searching: boolean = false;
    public loading: boolean = false;
    public loadingFilter: boolean = false;
    public clientPage: number = 1;
    public readonly ITEMS_PER_PAGE = 20;
    public loaders: any = {
        calendar: false
    };
    public mode: string = 'list';
    public viewMode: 'grid' | 'list' = 'grid';
    public sidebarCollapsed: boolean = false;
    public activeSaved: boolean = false;
    public query: string;
    public filters: any = {};
    public activeFilters: boolean = false;
    public cardParams: any;
    public hasError: boolean = false;
    // [SCROLL-TO-TOP] Controla la visibilidad del botón de volver arriba
    public showScrollTop: boolean = false;
    // Propiedades para el carrusel de filtros
    public availableTypes: any[] = [];
    public availableLanguages: any[] = [];
    public availableProviders: any[] = [];
    public filtersModal: any;
    public isLoadingFilters: boolean = true;
    /* ===== RESPALDO CARRUSEL: CONTROLES DESLIZABLES =====
    public canScrollLeft: boolean = false;
    public canScrollRight: boolean = false;
    */
    // Variables para rastrear cambios de filtros desde Filter1
    private lastFilterState: any = null;
    private searchSubscription: any;
    // Variables para rastrear cambios de filtros (mutua exclusividad)
    private lastSelectedTypesCount: number = 0;
    private lastSelectedProvidersCount: number = 0;

    // Status / progress filter counts
    public statusCounts: { inProgress: number; completed: number; notStarted: number; total: number } = 
        { inProgress: 0, completed: 0, notStarted: 0, total: 0 };


    constructor(
        private route: ActivatedRoute,
        private contentProvider: ContentProvider,
        private storageService: StorageService,
        private loaderService: LoaderService,
        private emitterService: EmitterService,
        private paramsService: ParamsService,
        private translateService: TranslateService
    ) { }

    ngOnInit(): void {
        this.me = this.storageService.get('me');
        this.company = this.storageService.get('company');
        this.contents = this.route.snapshot.data.contents.contents;
        this.allContents = [...this.contents]; // Store full list for status counting
        this.pagination = { ...this.route.snapshot.data.contents.pagination };
        this.calculateStatusCounts(this.allContents);

        this.cardParams = {
            isDeletable: true
        };

        this.getEvents();

        this.subscriptionCollection.subscribe = this.emitterService.get('search.contents').subscribe((query) => {
            if (typeof query === 'string') {
                this.contents = null;
                this.search(query);
                this.emitterService.set('search.contents', {});
            }
        });

        this.subscriptionCollection.subscribe = this.emitterService.newResourceAdded.subscribe(() => {
            this.search();
        });

        // Escuchar evento de reset de filtros desde topbar-filters
        this.subscriptionCollection.subscribe = this.emitterService.get('filter.reset.carousel').subscribe(() => {
            this.onFilterReset();
        });

        this.getMeta(); // Cargar filtros para el carrusel
    }

    // Se ejecuta después de que Angular haya renderizado completamente la vista, garantizando que los elementos DOM están listos
    ngAfterViewInit(): void {
        // Anima las cards con efecto de entrada (fade, slide y scale)
        this.animateCards();
    }

    ngOnDestroy(): void {
        this.subscriptionCollection.unsubscribe();
    }

    // [SCROLL-TO-TOP] Escucha el scroll de la ventana; muestra el botón al superar 300px de desplazamiento
    @HostListener('window:scroll', [])
    onWindowScroll(): void {
        this.showScrollTop = window.scrollY > 300;
    }

    // [SCROLL-TO-TOP] Desplaza la página suavemente hasta el tope
    scrollToTop(): void {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // [SCROLL-TO-TOP] Devuelve true si el floating-chat IA está habilitado en la empresa,
    // para posicionar el botón por encima del ícono del chat y no sobreponerlo
    get isChatEnabled(): boolean {
        return (this.company as any)?.permissions?.companyAiAssistant === true;
    }

    ngDoCheck(): void {
        // Sincronizar cambios de filtros desde Filter1 (topbar-filters) a Filter2 (carousel)
        // Comparar si el array de contentTypes o providers cambió
        const filtersChanged = !this.lastFilterState || 
                              JSON.stringify(this.lastFilterState.contentTypes) !== JSON.stringify(this.filters.contentTypes) ||
                              JSON.stringify(this.lastFilterState.providers) !== JSON.stringify(this.filters.providers);
        
        if (filtersChanged) {
            this.syncFiltersFromTopbar();
            // Guardar estado actual
            this.lastFilterState = {
                contentTypes: this.filters.contentTypes ? [...this.filters.contentTypes] : [],
                providers: this.filters.providers ? [...this.filters.providers] : []
            };
        }
    }

    private syncFiltersFromTopbar(): void {
        // Actualizar directamente sin mapear todo el array
        if (this.filters.contentTypes && this.filters.contentTypes.length > 0) {
            for (let type of this.availableTypes) {
                type.isSelected = this.filters.contentTypes.includes(type.value);
            }
        } else {
            for (let type of this.availableTypes) {
                type.isSelected = false;
            }
        }

        // Actualizar providers sin regenerar el array
        if (this.filters.providers && this.filters.providers.length > 0) {
            for (let provider of this.availableProviders) {
                provider.isSelected = this.filters.providers.includes(provider.value);
            }

            // Actualizar también en filtersModal
            if (this.filtersModal) {
                for (let filter of this.filtersModal) {
                    if (filter.name === 'providers' && filter.options) {
                        for (let option of filter.options) {
                            option.isSelected = this.filters.providers.includes(option.value);
                        }
                    }
                }
            }
        } else {
            for (let provider of this.availableProviders) {
                provider.isSelected = false;
            }

            // Actualizar también en filtersModal
            if (this.filtersModal) {
                for (let filter of this.filtersModal) {
                    if (filter.name === 'providers' && filter.options) {
                        for (let option of filter.options) {
                            option.isSelected = false;
                        }
                    }
                }
            }
        }

        // Actualizar contadores y bandera de filtros activos de acuerdo al estado sincronizado
        this.updateCountersFromFiltersState();
    }

    private getEvents(): void {
        this.contentProvider.getEventsSubscribed().subscribe((data) => {
            this.paginationEvent = data.pagination;
            this.contentsCollection = data.contents;

            this.groupEvents(data.contents);
        });
    }

    private groupEvents(contents: any): void {
        for (const content of contents) {
            content.eventDate = moment(content.event.startAt).format('YYYY-MM-DD');
        }
        this.events = DataHelper.groupBy(this.contentsCollection, 'eventDate', { key: 'date', value: 'items' });
        this.loaders.calendar = false;
    }
    /*
        search(query?: string): void {
            this.searching = true;
            this.hasError = false; // Resetear error al iniciar búsqueda
            this.pagination = null;
            this.loaderService.run();
    
            const params = this.paramsService.getCatalogue(this.filters, this.company.id, this.query || query, null, { mode: this.mode }, this.me);
    
            this.contentProvider.getContents(params).subscribe({
                next: (data) => {
                    this.pagination = data.pagination;
                    this.contents = data.contents;
                    this.searching = false;
    
                    this.loaderService.stop(); // detiene el loader
                },
                error: (err) => {
                    if (err.status === 404) {
                        this.hasError = true; // Manejar error 404
                    }
                    this.searching = false;
                    this.loaderService.stop(); // detiene el loader
                }
            });
        }*/

    search(query?: string): void {
        this.hasError = false;
        // Si cambia el filtro de progreso, mantener la página actual
        const prevProgress = this.filters.progressStatus ? [...this.filters.progressStatus] : [];
        const hasProgressStatusFilter = !!(this.filters.progressStatus && this.filters.progressStatus.length > 0);

        // Detectar si hay filtros reales que requieren llamada API
        const hasRealFilters = !!(query || this.query) ||
            (this.filters.contentTypes?.length > 0) ||
            (this.filters.providers?.length > 0) ||
            (this.filters.langs?.length > 0) ||
            (this.filters.contentSkills?.length > 0) ||
            (this.filters.order && this.filters.order !== '-date');

        // Sin filtros reales: aplicar filtro de progreso sobre todos los datos ya cargados
        if (!hasRealFilters) {
            if (!hasProgressStatusFilter) {
                // Sin ningún filtro — restaurar desde snapshot (página actual)
                const originalContents = this.route.snapshot.data.contents.contents;
                this.allContents = [...originalContents];
                this.pagination = { ...this.route.snapshot.data.contents.pagination };
                this.calculateStatusCounts(this.allContents);
                this.contents = [...originalContents];
            } else {
                // Solo filtro de progreso — cargar TODAS las páginas primero, luego filtrar
                if (!this.allContents?.length) {
                    const originalContents = this.route.snapshot.data.contents.contents;
                    this.allContents = [...originalContents];
                    this.pagination = { ...this.route.snapshot.data.contents.pagination };
                }

                const applyFilter = () => {
                    this.calculateStatusCounts(this.allContents);
                    this.contents = this.applyStatusFilter(this.allContents, this.filters.progressStatus);
                    this.clientPage = 1;
                    window.scrollTo({ top: 0, behavior: 'auto' });
                };

                if (this.pagination?.currentPage < this.pagination?.lastPage) {
                    this.loading = true;
                    this.loadingFilter = true;
                    this.loadRemainingContents(() => {
                        this.loading = false;
                        this.loadingFilter = false;
                        if (this.pagination) {
                            this.pagination.currentPage = this.pagination.lastPage;
                        }
                        applyFilter();
                    });
                } else {
                    applyFilter();
                }
            }
            // No reiniciar paginación salvo que cambie el filtro
            return;
        }

        // Con filtros reales: llamada API
        this.searching = true;
        this.loaderService.run();

        const paramsFilters = { ...this.filters };
        delete paramsFilters.progressStatus;

        const params = this.paramsService.getCatalogue(
            paramsFilters,
            this.company.id,
            query || this.query,
            null,
            { mode: this.mode },
            this.me
        );

        if (this.searchSubscription) { this.searchSubscription.unsubscribe(); }
        this.searchSubscription = this.contentProvider.getContents(params).subscribe({
            next: (data) => {
                this.pagination = data.pagination;

                // Filtrar por título ignorando acentos cuando hay query activo
                const searchQuery = (query || this.query || '').trim();
                const normalize = (s: string): string =>
                    (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                const normalizedQuery = normalize(searchQuery);
                this.allContents = normalizedQuery
                    ? (data.contents || []).filter((item: any) => {
                        const title = normalize(item.data?.title || item.title || '');
                        return title.includes(normalizedQuery);
                    })
                    : [...data.contents];

                const doFinish = () => {
                    this.calculateStatusCounts(this.allContents);
                    this.contents = hasProgressStatusFilter
                        ? this.applyStatusFilter(this.allContents, this.filters.progressStatus)
                        : [...this.allContents];
                    this.searching = false;
                    this.loadingFilter = false;
                    this.loaderService.stop();
                    setTimeout(() => this.animateCards(), 50);
                };

                if (hasProgressStatusFilter && this.pagination?.currentPage < this.pagination?.lastPage) {
                    this.loading = true;
                    this.loadingFilter = true;
                    this.loadRemainingContents(() => {
                        this.loading = false;
                        if (this.pagination) {
                            this.pagination.currentPage = this.pagination.lastPage;
                        }
                        doFinish();
                    });
                } else {
                    doFinish();
                }
            },
            error: (err) => {
                if (err.status === 404) {
                    this.hasError = true;
                }
                this.searching = false;
                this.loadingFilter = false;
                this.loaderService.stop();
            }
        });
    }

    private animateCards(): void {
        // Selecciona el selector CSS basado en el modo de vista (grid o lista)
        const selector = this.viewMode === 'grid'
            ? '.grid-catalogue .relative'
            : '.card-course-grid content-card-list';
        // Obtiene todos los elementos de las cards del DOM
        const cards = Array.from(document.querySelectorAll(selector));
        // Si no hay cards, sale del método
        if (!cards.length) return;
        // Anima las cards con GSAP: fade in, slide up, y scale de entrada
        gsap.fromTo(cards,
            { opacity: 0, y: 28, scale: 0.96 },
            { opacity: 1, y: 0, scale: 1, duration: 0.45, stagger: 0.055, ease: 'power2.out', clearProps: 'all', overwrite: 'auto' }
        );
    }

    loadMore(): void {
        if (this.loading) { return; }

        const hasMorePages = this.pagination?.currentPage < this.pagination?.lastPage;
        if (!hasMorePages) { return; }

        this.loading = true;
        this.hasError = false;

        // Build params without progressStatus (not supported by API)
        const paramsFilters = { ...this.filters };
        delete paramsFilters.progressStatus;

        const params = this.paramsService.getCatalogue(paramsFilters, this.company.id, null, this.pagination, { mode: this.mode }, this.me);

        const hasProgressStatusFilter = this.filters.progressStatus && this.filters.progressStatus.length > 0;

        this.contentProvider.getContents(params).subscribe({
            next: (data) => {
                this.pagination = data.pagination;
                // Always add to allContents for accurate status counting
                this.allContents = this.allContents.concat(data.contents);
                this.calculateStatusCounts(this.allContents);
                // Apply status filter client-side if active
                if (hasProgressStatusFilter) {
                    this.contents = this.applyStatusFilter(this.allContents, this.filters.progressStatus);
                } else {
                    this.contents = this.contents.concat(data.contents);
                }
                this.loading = false;
            },
            error: (err) => {
                if (err.status === 404) {
                    this.hasError = true; // Manejar error 404
                }
                this.loading = false;
            }
        });
    }

    get displayedContents(): any[] {
        const src = Array.isArray(this.contents) ? this.contents : [];
        const start = (this.clientPage - 1) * this.ITEMS_PER_PAGE;
        return src.slice(start, start + this.ITEMS_PER_PAGE);
    }

    get totalPages(): number {
        const src = Array.isArray(this.contents) ? this.contents : [];
        const hasFilter = this.activeRanking || (this.filters?.progressStatus?.length > 0);
        const apiTotal = !hasFilter ? (this.pagination?.total ?? src.length) : src.length;
        return Math.max(1, Math.ceil(apiTotal / this.ITEMS_PER_PAGE));
    }

    get visiblePages(): number[] {
        const total = this.totalPages;
        if (total <= 5) { return Array.from({ length: total }, (_, i) => i + 1); }
        let start = Math.max(1, this.clientPage - 2);
        const end = Math.min(total, start + 4);
        start = Math.max(1, end - 4);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }

    public goToPage(page: number): void {
        if (page < 1 || page > this.totalPages || this.loading) { return; }
        const neededCount = page * this.ITEMS_PER_PAGE;
        if (neededCount > this.allContents.length && this.pagination?.currentPage < this.pagination?.lastPage) {
            this.loading = true;
            this.loadUntilEnoughContents(neededCount, () => {
                this.loading = false;
                if (!this.activeRanking && !(this.filters?.progressStatus?.length > 0)) {
                    this.contents = [...this.allContents];
                }
                this.clientPage = page;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        } else {
            this.clientPage = page;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    private loadUntilEnoughContents(neededCount: number, onComplete: () => void): void {
        if (this.allContents.length >= neededCount || !(this.pagination?.currentPage < this.pagination?.lastPage)) {
            onComplete();
            return;
        }
        const paramsFilters = { ...this.filters };
        delete paramsFilters.progressStatus;
        const params = this.paramsService.getCatalogue(paramsFilters, this.company.id, null, this.pagination, { mode: this.mode }, this.me);
        this.contentProvider.getContents(params).subscribe({
            next: (data) => {
                this.allContents = this.allContents.concat(data.contents);
                this.calculateStatusCounts(this.allContents);
                this.pagination = data.pagination;
                this.loadUntilEnoughContents(neededCount, onComplete);
            },
            error: () => { onComplete(); }
        });
    }

    private loadRemainingContents(onComplete: () => void): void {
        if (!(this.pagination?.currentPage < this.pagination?.lastPage)) {
            onComplete();
            return;
        }
        const currentPage = this.pagination.currentPage;
        const lastPage = this.pagination.lastPage;
        const perPage = this.pagination.perPage;
        const paramsFilters = { ...this.filters };
        delete paramsFilters.progressStatus;

        const pageRequests = [];
        // paramsService.getCatalogue sends page = currentPage + 1, so pass p = target_page - 1
        for (let p = currentPage; p < lastPage; p++) {
            const mockPagination = { currentPage: p, perPage } as any;
            const params = this.paramsService.getCatalogue(paramsFilters, this.company.id, this.query, mockPagination, { mode: this.mode }, this.me);
            pageRequests.push(
                this.contentProvider.getContents(params).pipe(catchError(() => of({ contents: [] })))
            );
        }

        forkJoin(pageRequests).subscribe({
            next: (results: any[]) => {
                results.forEach((data: any) => {
                    this.allContents = this.allContents.concat(data.contents || []);
                });
                onComplete();
            },
            error: () => { onComplete(); }
        });
    }

    loadMoreEvents(): void {
        const params = {
            page: this.paginationEvent.currentPage + 1
        };
        this.loaders.calendar = true;

        this.contentProvider.getEventsSubscribed(params).subscribe((data) => {
            this.paginationEvent = data.pagination;
            this.contentsCollection = this.contentsCollection.concat(data.contents);

            this.groupEvents(data);
        });
    }

    deleteContent(index: number): void {
        this.contents.splice(index, 1);
        this.getEvents();
    }

    // Métodos para el carrusel de filtros
    private getMeta(): void {
        this.contentProvider.getMeta('content').subscribe((meta) => {
            this.availableTypes = this.getTypesOptions(meta);
            this.availableLanguages = this.getLanguagesOptions(meta.languages);
            this.availableProviders = this.getProvidersOptions(meta.providers);
            this.applyExistingFiltersState();
            this.setFilters();
            this.isLoadingFilters = false;
        });
    }

    private getTypesOptions(data: any) {
        return Object.keys(data.contentTypes)
            .map((type) => ({
                id: `type-${type}`,
                label: this.translateService.instant(`api.content-type.${type.split(/(?=[A-Z])/).join('_').toLowerCase()}`),
                value: type.split(/(?=[A-Z])/).join('_').toLowerCase(),
                isSelected: false
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    private getProvidersOptions(data: any) {
        const label = this.company?.experience
            ? (this.company.experience.displayName ? this.company.experience.displayName : this.company.experience.name)
            : this.company?.name;

        return data
            .filter(provider => provider && (provider.id === 'company' || provider.name))
            .map((provider) => ({
                label: provider.id === 'company' ? label : provider.name,
                value: provider.id,
                isSelected: false
            }));
    }

    private getLanguagesOptions(data: any) {
        return Object.keys(data).map((lang) => ({
            id: lang,
            label: data[lang],
            isSelected: false
        }));
    }

    /**
     * Sincroniza el estado inicial de selección con los filtros ya activos (ej. topbar)
     * y prepara los contadores para la lógica de mutua exclusividad.
     */
    private applyExistingFiltersState(): void {
        const selectedTypes = Array.isArray(this.filters?.contentTypes) ? this.filters.contentTypes : [];
        const selectedProviders = Array.isArray(this.filters?.providers) ? this.filters.providers : [];

        if (this.availableTypes?.length) {
            this.availableTypes = this.availableTypes.map((type) => ({
                ...type,
                isSelected: selectedTypes.includes(type.value)
            }));
        }

        if (this.availableProviders?.length) {
            this.availableProviders = this.availableProviders.map((provider) => ({
                ...provider,
                isSelected: selectedProviders.includes(provider.value)
            }));
        }

        this.updateCountersFromFiltersState();
    }

    private setFilters(): void {
        this.filtersModal = [
            {
                name: 'langs',
                type: 'multiselect',
                label: this.translateService.instant('words.languages'),
                displayValue: this.availableLanguages.filter(lang => lang.isSelected).map(lang => lang.label).join(', '),
                options: this.availableLanguages
            },
            {
                name: 'providers',
                type: 'checkbox',
                label: this.translateService.instant('words.providers'),
                options: this.availableProviders
            }
        ];
    }

    public onSelectType(option) {
        // 1. Toggle en el objeto concreto
        option.isSelected = !option.isSelected;

        console.log('[LIST] onSelectType - Tipo seleccionado:', option.label, 'isSelected:', option.isSelected);
        console.log('[LIST] onSelectType - Todos los tipos:', this.availableTypes.map(t => ({ label: t.label, isSelected: t.isSelected })));
        
        this.saveFilters();
    }

    public onInlineMultiselectChange(option, filterName: string, optionsArray: any[]) {
        // 1. Toggle en el objeto concreto desde availableProviders
        if (filterName === 'providers') {
            // Buscar el proveedor en availableProviders por su value (que es el provider.id)
            const index = this.availableProviders.findIndex(p => p.value === option.value);
            if (index !== -1) {
                // Toglear isSelected
                this.availableProviders[index].isSelected = !this.availableProviders[index].isSelected;
                // Forzar change detection creando una nueva referencia del array
                this.availableProviders = [...this.availableProviders];
                
                console.log('[LIST] onInlineMultiselectChange - Proveedor toggled:', option.label);
                console.log('[LIST] onInlineMultiselectChange - isSelected:', this.availableProviders[index].isSelected);
                console.log('[LIST] onInlineMultiselectChange - Proveedores seleccionados:', this.availableProviders.filter(p => p.isSelected).map(p => ({ value: p.value, label: p.label })));
            }
        }

        this.saveFilters();
    }

    public onClearTypeChip(option: any, event: Event): void {
        event.stopPropagation();
        event.preventDefault();

        if (!option?.isSelected) {
            return;
        }

        option.isSelected = false;
        this.availableTypes = [...this.availableTypes];

        this.saveFilters();
    }

    public onClearProviderChip(option: any, event: Event): void {
        event.stopPropagation();
        event.preventDefault();

        const index = this.availableProviders.findIndex(provider => provider.value === option.value);
        if (index === -1 || !this.availableProviders[index].isSelected) {
            return;
        }

        this.availableProviders[index].isSelected = false;
        this.availableProviders = [...this.availableProviders];

        if (this.filtersModal) {
            this.filtersModal = this.filtersModal.map(filter => {
                if (filter.name === 'providers' && filter.options?.length) {
                    filter.options = filter.options.map(optionItem => ({
                        ...optionItem,
                        isSelected: optionItem.value === option.value ? false : optionItem.isSelected
                    }));
                }
                return filter;
            });
        }

        this.saveFilters();
    }

    private saveFilters(): void {
        const previousTypesCount = this.lastSelectedTypesCount || 0;
        const previousProvidersCount = this.lastSelectedProvidersCount || 0;

        // Construir listas actuales
        let contentTypeValues = this.availableTypes.filter(t => t.isSelected).map(t => t.value);
        let providerValues = this.availableProviders.filter(p => p.isSelected).map(p => p.value);

        // MUTUA EXCLUSIVIDAD: si antes no había tipos y ahora sí, limpiar proveedores
        if (previousTypesCount === 0 && contentTypeValues.length > 0 && providerValues.length > 0) {
            this.clearProvidersFilter();
            providerValues = [];
        }

        // MUTUA EXCLUSIVIDAD: si antes no había proveedores y ahora sí, limpiar tipos
        if (previousProvidersCount === 0 && providerValues.length > 0 && contentTypeValues.length > 0) {
            this.clearContentTypesFilter();
            contentTypeValues = [];
        }

        // Actualizar filtros con el estado final
        this.filters = DataHelper.merge(this.filters, {
            contentTypes: contentTypeValues
        });
        
        this.filters = DataHelper.merge(this.filters, {
            providers: providerValues
        });

        // Actualizar contadores y flag de filtros activos
        this.lastSelectedTypesCount = contentTypeValues.length;
        this.lastSelectedProvidersCount = providerValues.length;
        this.activeFilters = (this.lastSelectedTypesCount + this.lastSelectedProvidersCount) > 0;

        console.log('[LIST] saveFilters - activeFilters:', this.activeFilters);
        console.log('[LIST] saveFilters - contentTypes enviados:', this.filters.contentTypes);
        console.log('[LIST] saveFilters - providers enviados:', this.filters.providers);
        
        this.search();
    }

    // Métodos para limpiar filtros cruzados
    private clearProvidersFilter(): void {
        console.log('[LIST] clearProvidersFilter - INICIANDO');
        // Crear NUEVO array de proveedores para forzar change detection
        this.availableProviders = this.availableProviders.map(provider => {
            const newProvider = Object.assign({}, provider);
            newProvider.isSelected = false;
            return newProvider;
        });
        // Forzar actualización usando Object.assign
        this.availableProviders = Object.assign([], this.availableProviders);
        // Limpiar el filtro en filters
        this.filters = DataHelper.merge(this.filters, { providers: [] });
        this.lastSelectedProvidersCount = 0;
        this.activeFilters = (this.lastSelectedTypesCount + this.lastSelectedProvidersCount) > 0;

        // Sincronizar filtersModal si existe
        if (this.filtersModal) {
            this.filtersModal = this.filtersModal.map(filter => {
                if (filter.name === 'providers' && filter.options?.length) {
                    filter.options = filter.options.map(option => ({
                        ...option,
                        isSelected: false
                    }));
                }
                return filter;
            });
        }
        console.log('[LIST] clearProvidersFilter - COMPLETADO. Proveedores:', this.availableProviders.map(p => ({ label: p.label, isSelected: p.isSelected })));
    }

    private clearContentTypesFilter(): void {
        console.log('[LIST] clearContentTypesFilter - INICIANDO');
        // Crear NUEVO array de tipos para forzar change detection
        this.availableTypes = this.availableTypes.map(type => {
            const newType = Object.assign({}, type);
            newType.isSelected = false;
            return newType;
        });
        // Forzar actualización usando Object.assign
        this.availableTypes = Object.assign([], this.availableTypes);
        this.filters = DataHelper.merge(this.filters, { contentTypes: [] });
        this.lastSelectedTypesCount = 0;
        this.activeFilters = (this.lastSelectedTypesCount + this.lastSelectedProvidersCount) > 0;
        console.log('[LIST] clearContentTypesFilter - COMPLETADO. Tipos:', this.availableTypes.map(t => ({ label: t.label, isSelected: t.isSelected })));
    }

    /* ===== RESPALDO CARRUSEL: CONTROLES DE DESPLAZAMIENTO =====
    public scrollCategoriesLeft(): void {
        const container = document.getElementById('category-container');
        if (container) {
            container.scrollBy({ left: -200, behavior: 'smooth' });
            // setTimeout(() => this.updateScrollButtons(), 300);
        }
    }

    public scrollCategoriesRight(): void {
        const container = document.getElementById('category-container');
        if (container) {
            container.scrollBy({ left: 200, behavior: 'smooth' });
            // setTimeout(() => this.updateScrollButtons(), 300);
        }
    }

    public onCategoryContainerScroll(): void {
        this.updateScrollButtons();
    }

    private updateScrollButtons(): void {
        const container = document.getElementById('category-container');
        if (container) {
            // Verificar si hay contenido que desplazar hacia la izquierda
            this.canScrollLeft = container.scrollLeft > 0;
            
            // Verificar si hay contenido que desplazar hacia la derecha
            this.canScrollRight = container.scrollLeft + container.clientWidth < container.scrollWidth;
        }
    }
    ===== FIN RESPALDO ===== */

    /**
     * Resetea todos los filtros (tipos de contenido y proveedores)
     * Llamado desde el botón de reset de topbar-filters o cuando el evento es emitido
     */
    public onFilterReset(): void {
        // Resetear array de tipos
        this.availableTypes = this.availableTypes.map(type => {
            const newType = Object.assign({}, type);
            newType.isSelected = false;
            return newType;
        });
        this.availableTypes = Object.assign([], this.availableTypes);

        // Resetear array de proveedores
        this.availableProviders = this.availableProviders.map(provider => {
            const newProvider = Object.assign({}, provider);
            newProvider.isSelected = false;
            return newProvider;
        });
        this.availableProviders = Object.assign([], this.availableProviders);

        // Resetear filtros modal
        if (this.filtersModal) {
            this.filtersModal = this.filtersModal.map(filter => {
                if ((filter.name === 'providers' || filter.name === 'langs') && filter.options?.length) {
                    filter.options = filter.options.map(option => ({
                        ...option,
                        isSelected: false
                    }));
                }
                return filter;
            });
        }

        // Resetear estado de filtros
        this.filters = {};
        this.lastSelectedTypesCount = 0;
        this.lastSelectedProvidersCount = 0;
        this.activeFilters = false;

        console.log('[LIST] onFilterReset - Filtros reseteados completamente');
        
        // Llamar a search para recargar sin filtros
        this.search();
    }

    // TrackBy para mejorar detección de cambios en *ngFor
    public trackByValue(index: number, item: any): any {
        return item.value || item.id || index;
    }

    /**
     * Ajusta los contadores según el estado real de los filtros (útil al sincronizar con topbar)
     */
    private updateCountersFromFiltersState(): void {
        this.lastSelectedTypesCount = Array.isArray(this.filters?.contentTypes) ? this.filters.contentTypes.length : 0;
        this.lastSelectedProvidersCount = Array.isArray(this.filters?.providers) ? this.filters.providers.length : 0;
        this.activeFilters = (this.lastSelectedTypesCount + this.lastSelectedProvidersCount) > 0;
    }

    public onViewModeChange(mode: 'grid' | 'list'): void {
        this.viewMode = mode;
    }

    // ===== SORT & RANKING (search-sort-bar) =====
    public activeRanking: string = '';

    public onSortChange(filters: any): void {
        this.activeRanking = '';
        if (filters.order === 'title') {
            this.contents = [...this.contents].sort((a, b) =>
                (a.data?.title || '').localeCompare(b.data?.title || ''));
        } else if (filters.order === '-title') {
            this.contents = [...this.contents].sort((a, b) =>
                (b.data?.title || '').localeCompare(a.data?.title || ''));
        } else {
            this.search();
        }
    }

    public onRankingChange(ranking: string): void {
        // Ranking y guardados son excluyentes - desmarcar saved cuando se activa ranking
        if (ranking) {
            this.activeSaved = false;
            // Actualizar el estado en search-sort-bar
            const savedButton = document.querySelector('[data-test="saved-button"]');
            if (savedButton) {
                savedButton.classList.remove('active');
            }
        }
        this.activeRanking = ranking;

        // Reset ranking: restore original data
        if (!ranking) {
            const src = this.allContents.length ? this.allContents : [];
            this.contents = [...src];
            this.clientPage = 1;
            return;
        }

        const applyRanking = () => {
            const src = this.allContents.length ? this.allContents : this.contents;
            const getRating = (item: any): number => Number(item.data?.rating) || 0;
            const isRated = (item: any): boolean => getRating(item) > 0;
            const isUnrated = (item: any): boolean => getRating(item) <= 0;
            if (ranking === 'top') {
                // Solo calificados (rating > 0), descendente
                this.contents = [...src].filter(isRated).sort((a, b) => getRating(b) - getRating(a));
            } else if (ranking === 'lowest') {
                // Solo calificados (rating > 0), ascendente
                this.contents = [...src].filter(isRated).sort((a, b) => getRating(a) - getRating(b));
            } else if (ranking === 'unrated') {
                // Solo sin calificar (rating <= 0)
                this.contents = [...src].filter(isUnrated);
            }
        };

        if (this.pagination?.currentPage < this.pagination?.lastPage) {
            this.loading = true;
            this.loadingFilter = true;
            this.loadRemainingContents(() => {
                this.loading = false;
                this.loadingFilter = false;
                if (this.pagination) {
                    this.pagination.currentPage = this.pagination.lastPage;
                }
                applyRanking();
                this.clientPage = 1;
            });
        } else {
            applyRanking();
            this.clientPage = 1;
        }
    }
    // ===== END SORT & RANKING =====

    public onSavedChange(active: boolean): void {
        // Guardados y ranking son excluyentes
        if (active) this.activeRanking = '';
        this.activeSaved = active;

        if (!active) {
            const originalContents = this.route.snapshot.data.contents.contents;
            this.allContents = [...originalContents];
            this.pagination = { ...this.route.snapshot.data.contents.pagination };
            this.calculateStatusCounts(this.allContents);
            this.contents = [...originalContents];
            return;
        }

        this.loadingFilter = true;
        const params = this.paramsService.getCatalogue({}, this.company.id, this.query, null, null, this.me);
        this.contentProvider.getSavedContents(params).subscribe({
            next: (data) => {
                this.contents = (data.contents || []).map((c: any) => ({ ...c, saved: true }));
                this.allContents = [...this.contents];
                this.pagination = data.pagination;
                this.calculateStatusCounts(this.allContents);
                this.loadingFilter = false;
            },
            error: () => {
                this.loadingFilter = false;
            }
        });
    }

    // ===== STATUS / PROGRESS FILTER METHODS =====

    /**
     * Calculates the count of courses by progress status
     * Progress = 0: not started, 0 < progress < 100: in progress, progress = 100: completed
     */
    public calculateStatusCounts(contents: any[]): void {
        if (!contents || contents.length === 0) {
            this.statusCounts = { inProgress: 0, completed: 0, notStarted: 0, total: 0 };
            return;
        }
        let inProgress = 0;
        let completed = 0;
        let notStarted = 0;

        for (const content of contents) {
            const progress = content?.data?.progress ?? 0;
            if (progress >= 100) {
                completed++;
            } else if (progress > 0) {
                inProgress++;
            } else {
                notStarted++;
            }
        }

        this.statusCounts = {
            inProgress,
            completed,
            notStarted,
            total: contents.length
        };
    }

    /**
     * Filters content array by progress status values
     * @param contents - The full contents array to filter
     * @param statuses - Array of status values: 'all', 'in_progress', 'completed', 'not_started'
     */
    public applyStatusFilter(contents: any[], statuses: string[]): any[] {
        if (!statuses || statuses.length === 0 || statuses.includes('all')) {
            return contents;
        }

        return contents.filter(content => {
            const progress = content?.data?.progress ?? 0;
            if (statuses.includes('completed') && progress >= 100) return true;
            if (statuses.includes('in_progress') && progress > 0 && progress < 100) return true;
            if (statuses.includes('not_started') && progress === 0) return true;
            return false;
        });
    }
    // ===== END STATUS / PROGRESS FILTER METHODS =====
}
